import React, { useEffect, useState } from 'react';
import { CircularProgress, Divider, Paper, Stack, Button, Typography } from '@mui/material';
import { ReactComponent as AssessmentOutlined } from 'assets/icons/report_icon.svg';
import { ReactComponent as GridViewOutlined } from 'assets/icons/grid_icon.svg';
import { useCampaign, useCampaignActionsByStatus, useReportingUrls } from 'hooks';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { OverviewHeader } from './components/OverviewHeader/OverviewHeader';
import { OverviewCreatives } from './components/OverviewCreatives';
import { OverviewReport } from './components/OverviewReport';
import { OverviewTargeting } from './components/OverviewTargeting/OverviewTargeting';
import { OverviewStats } from './components/OverviewStats/OverviewStats';
import { Error404 } from '../ErrorPage/views';
import { getCampaignStatus } from 'utils';
import { CampaignState, CampaignStatus } from 'types';
import { DateTime } from 'luxon';
import { ReactComponent as LoadStatsIllustration } from 'assets/images/load-stats.svg';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabsStyles = {
  height: '52px',
  fontSize: '20px',
  '& .MuiTabs-flexContainer': {
    height: '52px',
    '& .MuiTab-root': {
      minHeight: '52px',
      padding: '8px',
      fontSize: '20px',
      color: '#BB448B',

      '&.Mui-selected': {
        fontWeight: 'bold',
      },
    },
  },
  '& .MuiTabs-indicator': {
    height: '4px',
  },
};

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div {...other} role="tabpanel" hidden={value !== index}>
      {value === index ? children : null}
    </div>
  );
}

interface ReportNotReadyMessageProps {
  onGoToOverview: () => void;
}

const ReportNotReadyMessage: React.FC<ReportNotReadyMessageProps> = ({ onGoToOverview }) => {
  return (
    <Stack direction="column" alignItems="center" gap={2} py={4}>
      <LoadStatsIllustration style={{ width: '180px', height: '180px' }} />
      <Typography
        variant="h2"
        sx={{
          fontWeight: 400,
          textAlign: 'center',
        }}
      >
        Der Detailreport ist unterwegs –<br />
        spätestens 24 Stunden nach Kampagnenstart siehst du hier mehr.
        <br />
        Bis dahin findest du in der Übersicht weitere Infos.
      </Typography>
      <Button variant="text" onClick={onGoToOverview}>
        Zur übersicht
      </Button>
    </Stack>
  );
};

export const Overview = () => {
  const { getCampaignQuery } = useCampaign();
  const [tabIndex, setTabIndex] = useState(0);
  const campaign = getCampaignQuery.data?.campaign;
  const status = getCampaignStatus({
    state: campaign?.state || CampaignState.draft,
    startDate: campaign?.startDate || null,
    endDate: campaign?.endDate || null,
    isStopped: campaign?.isStopped,
  });
  const { isOverviewAvailable } = useCampaignActionsByStatus(status);

  const luxonCampaignStartDate = DateTime.fromISO(campaign?.startDate || '');
  const luxonNextDayAfterCampaignStart = luxonCampaignStartDate.plus({ days: 1 }).set({ hour: 8 });

  // looker will need at least 24 hours to add data to the report so we make it visible on the next day after campaign is started
  const isReportDataReady =
    [CampaignStatus.active, CampaignStatus.over].includes(status) && DateTime.now() > luxonNextDayAfterCampaignStart;

  const handleTabChange = (_: any, newValue: number) => {
    setTabIndex(newValue);
  };

  const { getReportingUrlsQuery } = useReportingUrls();
  const reportingUrls = getReportingUrlsQuery.data || { embedded: '', link: '' };

  useEffect(() => {
    getCampaignQuery.refetch();
    // This effect must runs only once when the component mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (getCampaignQuery.isLoading) {
    return <CircularProgress />;
  }

  if (!isOverviewAvailable && !getCampaignQuery.isRefetching) {
    return (
      <Paper sx={{ py: 10, px: 5 }}>
        <Stack spacing={5} alignItems="center">
          <Error404 />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper>
      <Stack p={5}>
        <OverviewHeader />
        <Divider
          sx={{
            margin: '20px -20px 32px -20px',
          }}
        />
        <Tabs
          textColor="primary"
          indicatorColor="primary"
          sx={{
            ...TabsStyles,
            width: {
              xs: '100%',
              sm: 'fit-content',
            },
            ' .MuiTab-root': {
              flexBasis: {
                xs: '50%',
                sm: 'initial',
              },
            },
          }}
          value={tabIndex}
          onChange={handleTabChange}
        >
          <Tab iconPosition="start" icon={<GridViewOutlined />} label="Übersicht" tabIndex={0} />
          {campaign?.reportId ? (
            <Tab iconPosition="start" icon={<AssessmentOutlined />} label="Detailreport" tabIndex={1} />
          ) : null}
        </Tabs>
        <Divider
          sx={{
            margin: '0 0 32px 0',
          }}
        />
        <CustomTabPanel value={tabIndex} index={0}>
          <OverviewStats />
          <OverviewCreatives />
          <OverviewTargeting />
        </CustomTabPanel>
        {campaign?.reportId && !isReportDataReady ? (
          <CustomTabPanel value={tabIndex} index={1}>
            <ReportNotReadyMessage onGoToOverview={() => setTabIndex(0)} />
          </CustomTabPanel>
        ) : null}
        {campaign?.reportId && isReportDataReady ? (
          <CustomTabPanel value={tabIndex} index={1}>
            <OverviewReport
              link={reportingUrls.link}
              embedded={reportingUrls.embedded}
              id={campaign?.id}
              status={status}
            />
          </CustomTabPanel>
        ) : null}
      </Stack>
    </Paper>
  );
};
